import type { AiContext } from './types/ai-context';

import { getPrV2FileElementId } from '@/app/pr-v2/[owner]/[repo]/pull/[number]/components/file-tree-v2/pr-v2-file-tree-utils';
import { isPrV2Route } from '@/lib/pr-v2/route-utils';
import {
  getPrV2FileViewportTopOffset,
  PR_V2_BASE_FILE_VIEWPORT_TOP_OFFSET,
} from '@/lib/pr-v2/sticky-tabs-offset';

type FileRectElement = {
  getBoundingClientRect: () => {
    top: number;
    bottom: number;
  };
};

type ResolveTopVisiblePrV2FilenameOptions = {
  getElementById?: (id: string) => FileRectElement | null;
  getViewportTopOffset?: (element: FileRectElement) => number;
  viewportBottom?: number;
  viewportTopOffset?: number;
};

type BuildSidebarChatAiContextOptions = ResolveTopVisiblePrV2FilenameOptions & {
  pathname?: string;
};

function getChatContextFilenames(aiContext: AiContext): string[] {
  const filenames = [
    ...(aiContext.files?.map((file) => file.filename) ?? []),
    ...(aiContext.codeContent?.map((file) => file.filename) ?? []),
  ].filter((filename): filename is string => typeof filename === 'string' && filename.length > 0);

  return Array.from(new Set(filenames));
}

function resolveFileViewportTopOffset(
  element: FileRectElement,
  options: ResolveTopVisiblePrV2FilenameOptions,
): number {
  if (options.viewportTopOffset != null) {
    return options.viewportTopOffset;
  }

  const resolvedViewportTopOffset = options.getViewportTopOffset?.(element);
  if (typeof resolvedViewportTopOffset === 'number' && Number.isFinite(resolvedViewportTopOffset)) {
    return resolvedViewportTopOffset;
  }

  if (typeof Element !== 'undefined' && element instanceof Element) {
    return getPrV2FileViewportTopOffset(element);
  }

  return PR_V2_BASE_FILE_VIEWPORT_TOP_OFFSET;
}

export function resolveTopVisiblePrV2Filename(
  renderedFilenames: readonly string[],
  options: ResolveTopVisiblePrV2FilenameOptions = {},
): string | null {
  const getElementById =
    options.getElementById ??
    ((id: string) =>
      typeof document === 'undefined'
        ? null
        : (document.getElementById(id) as FileRectElement | null));
  const viewportBottom =
    options.viewportBottom ?? (typeof window === 'undefined' ? null : window.innerHeight);

  if (viewportBottom == null) {
    return null;
  }

  let topVisibleFilename: string | null = null;
  let topVisibleOffset = Number.POSITIVE_INFINITY;

  renderedFilenames.forEach((filename) => {
    const element = getElementById(getPrV2FileElementId(filename));
    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const viewportTopOffset = resolveFileViewportTopOffset(element, options);
    const isVisible = rect.bottom > viewportTopOffset && rect.top < viewportBottom;
    if (!isVisible) {
      return;
    }

    if (rect.top < topVisibleOffset) {
      topVisibleOffset = rect.top;
      topVisibleFilename = filename;
    }
  });

  return topVisibleFilename;
}

/** Resolves only the file that is currently visible, without applying chat-context fallbacks. */
export function resolveTopVisiblePrV2ChatFilename(
  aiContext: AiContext,
  options: ResolveTopVisiblePrV2FilenameOptions = {},
): string | null {
  return resolveTopVisiblePrV2Filename(getChatContextFilenames(aiContext), options);
}

export function buildSidebarChatAiContext(
  aiContext: AiContext,
  options: BuildSidebarChatAiContextOptions = {},
): AiContext {
  const pathname =
    options.pathname ?? (typeof window === 'undefined' ? '' : window.location.pathname);

  if (!isPrV2Route(pathname)) {
    return aiContext;
  }

  const visibleFilename =
    resolveTopVisiblePrV2ChatFilename(aiContext, options) ?? aiContext.visibleFilename;

  return {
    ...aiContext,
    visibleFilename: visibleFilename ?? undefined,
  };
}
